import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ArchiveManagement.css';

function ArchiveManagement() {
  const [archives, setArchives] = useState([]);
  const [stats, setStats] = useState(null);
  const [deletionHistory, setDeletionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveDetails, setArchiveDetails] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [retentionDays, setRetentionDays] = useState(90);
  const token = localStorage.getItem('token');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [archivesRes, statsRes, deletionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API}/api/admin/archives`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API}/api/admin/archives/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API}/api/admin/archives/deletions/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (archivesRes.ok) {
        const data = await archivesRes.json();
        setArchives(data.archives);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (deletionsRes.ok) {
        const data = await deletionsRes.json();
        setDeletionHistory(data.deletions || []);
      }
    } catch (err) {
      console.error('Failed to load archive data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runArchival = async () => {
    if (!window.confirm(`Archive logs older than ${retentionDays} days?`)) return;
    
    setArchiving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/archives/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ retentionDays })
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Archival completed! ${JSON.stringify(data.summary.archived)}`);
        loadData();
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to run archival');
      }
    } catch (err) {
      alert('Failed to run archival');
    } finally {
      setArchiving(false);
    }
  };

  const viewArchive = async (archiveId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/archives/${archiveId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setArchiveDetails(data);
        setSelectedArchive(archiveId);
      } else {
        alert('Failed to load archive');
      }
    } catch (err) {
      alert('Failed to load archive');
    }
  };

  const restoreArchive = async (archiveId) => {
    if (!window.confirm('Restore this archive to active logs?')) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/archives/${archiveId}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Restored ${data.restored} entries to ${data.category} logs`);
        setSelectedArchive(null);
        setArchiveDetails(null);
        loadData();
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to restore archive');
      }
    } catch (err) {
      alert('Failed to restore archive');
    }
  };

  const deleteArchive = async () => {
    if (!deleteReason || deleteReason.trim().length < 10) {
      alert('Deletion reason must be at least 10 characters');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/archives/${deleteModal}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: deleteReason })
      });

      if (res.ok) {
        alert('Archive deleted permanently');
        setDeleteModal(null);
        setDeleteReason('');
        if (selectedArchive === deleteModal) {
          setSelectedArchive(null);
          setArchiveDetails(null);
        }
        loadData();
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to delete archive');
      }
    } catch (err) {
      alert('Failed to delete archive');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="archive-management">
      <div className="archive-header">
        <div>
          <h2>Log Archive Management</h2>
          <p className="archive-subtitle">Automated 90-day retention • Compressed storage • Super Admin only</p>
        </div>
        <div className="archive-actions">
          <div className="retention-selector">
            <label htmlFor="retention-days">Retention Days:</label>
            <input
              id="retention-days"
              type="number"
              min="30"
              max="365"
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value))}
              aria-label="Number of days to retain logs"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={runArchival}
            disabled={archiving}
            aria-label={archiving ? 'Archiving logs' : 'Run archival now'}
          >
            {archiving ? 'Archiving...' : 'Run Archival Now'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container" role="status" aria-live="polite">
          <div className="loading-spinner" aria-label="Loading archive data"></div>
        </div>
      ) : (
        <>
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.totalArchives}</div>
                <div className="stat-label">Total Archives</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.totalEntries.toLocaleString()}</div>
                <div className="stat-label">Archived Entries</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatBytes(stats.totalCompressedSize)}</div>
                <div className="stat-label">Storage Used</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.averageCompressionRatio}</div>
                <div className="stat-label">Space Saved</div>
              </div>
            </div>
          )}

          <div className="archives-list">
            <h3>Archives ({archives.length})</h3>
            {archives.length === 0 ? (
              <div className="empty-state" role="status">
                No archives yet. Archives are created automatically when logs exceed {retentionDays} days.
              </div>
            ) : (
              <div className="archive-items" role="list">
                {archives.map((archive) => (
                  <motion.div
                    key={archive.id}
                    className="archive-item"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    layout
                    role="listitem"
                  >
                    <div className="archive-info">
                      <div className="archive-category">{archive.category}</div>
                      <div className="archive-meta">
                        {archive.count.toLocaleString()} entries • {formatBytes(archive.compressedSize)} • {archive.compressionRatio} saved
                      </div>
                      <div className="archive-date">
                        Archived: {new Date(archive.archivedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="archive-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => viewArchive(archive.id)}
                        aria-label={`View ${archive.category} archive`}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => restoreArchive(archive.id)}
                        aria-label={`Restore ${archive.category} archive`}
                      >
                        Restore
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteModal(archive.id)}
                        aria-label={`Delete ${archive.category} archive`}
                      >
                        Delete
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {deletionHistory.length > 0 && (
            <div className="deletion-history">
              <h3>Deletion History ({deletionHistory.length})</h3>
              <div className="deletion-items">
                {deletionHistory.slice(0, 10).map((del, idx) => (
                  <div key={idx} className="deletion-item">
                    <div><strong>{del.category}</strong> archive ({del.entryCount} entries)</div>
                    <div>Deleted by: {del.actorEmail}</div>
                    <div>Reason: {del.reason}</div>
                    <div className="deletion-date">{new Date(del.deletedAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Archive Detail Modal */}
      <AnimatePresence>
        {selectedArchive && archiveDetails && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedArchive(null);
                setArchiveDetails(null);
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-modal-title"
          >
            <motion.div
              className="modal-content archive-modal"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="archive-modal-title">{archiveDetails.category} Archive</h3>
                <button 
                  onClick={() => { setSelectedArchive(null); setArchiveDetails(null); }}
                  aria-label="Close archive details"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="archive-summary">
                  <div><strong>Total Entries:</strong> {archiveDetails.count.toLocaleString()}</div>
                  <div><strong>Date Range:</strong> {new Date(archiveDetails.dateRange.oldest).toLocaleDateString()} - {new Date(archiveDetails.dateRange.newest).toLocaleDateString()}</div>
                </div>
                <div className="archive-entries" role="list">
                  {archiveDetails.entries.slice(0, 50).map((entry, idx) => (
                    <div key={idx} className="log-entry" role="listitem">
                      <div className="log-time">{new Date(entry.timestamp).toLocaleString()}</div>
                      <div className="log-action">{entry.action} • {entry.target}</div>
                      <div className="log-actor">by {entry.actorEmail}</div>
                    </div>
                  ))}
                  {archiveDetails.entries.length > 50 && (
                    <div className="more-entries" role="status">
                      ... and {(archiveDetails.entries.length - 50).toLocaleString()} more entries
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setDeleteModal(null);
                setDeleteReason('');
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
          >
            <motion.div
              className="modal-content delete-modal"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="delete-modal-title">Permanently Delete Archive</h3>
                <button 
                  onClick={() => { setDeleteModal(null); setDeleteReason(''); }}
                  aria-label="Close delete confirmation"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Warning:</strong> This action cannot be undone. You must provide a reason.</p>
                <textarea
                  id="delete-reason"
                  placeholder="Reason for deletion (minimum 10 characters)..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={4}
                  aria-label="Deletion reason"
                  aria-required="true"
                  aria-describedby="delete-reason-help"
                />
                <div id="delete-reason-help" className="sr-only">
                  You must provide at least 10 characters explaining why you are deleting this archive
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => { setDeleteModal(null); setDeleteReason(''); }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={deleteArchive}
                  disabled={!deleteReason || deleteReason.trim().length < 10}
                  aria-label="Confirm permanent deletion"
                >
                  Delete Permanently
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ArchiveManagement;
